import React, { useState, useCallback } from 'react';
import { AlertCircle, Trash2, Plus } from 'lucide-react';
import { BiographyField as BiographyFieldType } from './types';
import './BiographySystem.css';

interface BiographyFieldProps {
  field: BiographyFieldType;
  value: string | string[];
  onChange: (value: string | string[]) => void;
  onRemove?: () => void;
  showLabel?: boolean;
  error?: string;
}

/**
 * BiographyField - Reusable field component that handles all field types
 * Supports text, textarea, list, readonly, and rich-text field types
 */
const BiographyField: React.FC<BiographyFieldProps> = ({
  field,
  value,
  onChange,
  onRemove,
  showLabel = true,
  error
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = event.target.value;

    // Apply validation if provided
    if (field.validation) {
      const validationError = field.validation(newValue);
      if (validationError) {
        return; // Don't update if validation fails
      }
    }

    onChange(newValue);
  }, [field.validation, onChange]);

  const handleListAdd = useCallback(() => {
    const currentList = Array.isArray(value) ? value : [];
    onChange([...currentList, '']);
  }, [value, onChange]);

  const handleListItemChange = useCallback((index: number, itemValue: string) => {
    const currentList = Array.isArray(value) ? value : [];
    const newList = [...currentList];
    newList[index] = itemValue;
    onChange(newList);
  }, [value, onChange]);

  const handleListItemRemove = useCallback((index: number) => {
    const currentList = Array.isArray(value) ? value : [];
    const newList = currentList.filter((_, i) => i !== index);
    onChange(newList);
  }, [value, onChange]);

  const getCharacterCount = () => {
    if (typeof value === 'string') {
      return value.length;
    }
    return 0;
  };

  const isNearLimit = field.maxLength && getCharacterCount() > field.maxLength * 0.8;
  const isOverLimit = field.maxLength && getCharacterCount() > field.maxLength;

  const fieldClassName = [
    'biography-field',
    error ? 'biography-field--error' : '',
    isFocused ? 'biography-field--focused' : ''
  ].filter(Boolean).join(' ');

  const renderField = () => {
    switch (field.type) {
      case 'text':
        return (
          <div className="biography-field__input-container">
            <input
              type="text"
              className="biography-field__input"
              value={typeof value === 'string' ? value : ''}
              onChange={handleInputChange}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder={field.placeholder}
              maxLength={field.maxLength}
              disabled={!field.editable}
            />
            {field.maxLength && (
              <div
                className={`biography-field__counter ${
                  isOverLimit ? 'biography-field__counter--error' :
                  isNearLimit ? 'biography-field__counter--warning' : ''
                }`}
              >
                {getCharacterCount()}/{field.maxLength}
              </div>
            )}
          </div>
        );

      case 'textarea':
        return (
          <div className="biography-field__input-container">
            <textarea
              className="biography-field__textarea"
              value={typeof value === 'string' ? value : ''}
              onChange={handleInputChange}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder={field.placeholder}
              maxLength={field.maxLength}
              disabled={!field.editable}
              rows={4}
            />
            {field.maxLength && (
              <div
                className={`biography-field__counter ${
                  isOverLimit ? 'biography-field__counter--error' :
                  isNearLimit ? 'biography-field__counter--warning' : ''
                }`}
              >
                {getCharacterCount()}/{field.maxLength}
              </div>
            )}
          </div>
        );

      case 'readonly':
        const displayValue = typeof value === 'string' ? value : Array.isArray(value) ? value.join(', ') : '';
        return (
          <div
            className={`biography-field__readonly ${
              !displayValue ? 'biography-field__readonly--empty' : ''
            }`}
          >
            {displayValue || field.placeholder || 'No information provided'}
          </div>
        );

      case 'list':
        const listItems = Array.isArray(value) ? value : [];
        return (
          <div className="biography-list">
            {listItems.map((item, index) => (
              <div key={index} className="biography-list__item">
                <div className="biography-list__item-content">
                  <input
                    type="text"
                    className="biography-field__input"
                    value={item}
                    onChange={(e) => handleListItemChange(index, e.target.value)}
                    placeholder={field.placeholder || `Item ${index + 1}`}
                    disabled={!field.editable}
                  />
                </div>
                {field.editable && (
                  <div className="biography-list__item-actions">
                    <button
                      type="button"
                      onClick={() => handleListItemRemove(index)}
                      className="biography-field__button biography-field__button--danger"
                      aria-label="Remove item"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>
            ))}
            {field.editable && (
              <button
                type="button"
                onClick={handleListAdd}
                className="biography-list__add-button"
              >
                <Plus size={14} />
                Add {field.label.toLowerCase()}
              </button>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={fieldClassName}>
      {showLabel && field.label && (
        <label
          className={`biography-field__label ${
            field.required ? 'biography-field__label--required' : ''
          }`}
        >
          {field.label}
        </label>
      )}

      {renderField()}

      {field.helpText && (
        <div className="biography-field__help-text">
          {field.helpText}
        </div>
      )}

      {error && (
        <div className="biography-field__error">
          <AlertCircle size={14} />
          {error}
        </div>
      )}

      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="biography-field__remove-button"
          aria-label={`Remove ${field.label}`}
        >
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
};

export default BiographyField;